import { useSelector } from "react-redux";

import { isDashmoteStaffEmail } from "../utils/isDashmoteStaffEmail";
import { isSyntheticAccountType } from "../utils/accountType";

/**
 * Whether the signed-in user is Dashmote staff for the purposes of the admin
 * portal: a staff address that is not a synthetic (demo/test) account.
 *
 * One hook so the rail and the route guard cannot answer this differently. They
 * previously did — the rail excluded synthetic accounts by testing the email for
 * the substring `demo`, and the guard did not exclude them at all — which let a
 * `test`-typed staff account open every admin page. `account_type` is the
 * structured answer, and `isSyntheticAccountType` mirrors the backend's
 * `SYNTHETIC_ACCOUNT_TYPES` so the runtimes agree.
 *
 * The email substring is kept as a fallback rather than replaced: a user doc
 * written before `account_type` existed carries no type, and dropping the older
 * heuristic would make such an account newly visible. Keeping both can only
 * narrow the result.
 *
 * **Not an authorization boundary.** The server decides — `is_dashmote_staff`
 * in `apps/api/src/utils/auth.py` takes the same two inputs and gates the data.
 * This hook decides what to render and which client routes to allow.
 *
 * `userData` arrives asynchronously (`ProtectedRoute` fetches it after first
 * paint), so an unknown `account_type` reads as non-synthetic. That fails open
 * for the moment before it loads — deliberately, because failing closed would
 * bounce legitimate staff off every page on each cold load. The server-side
 * check is what makes that safe.
 */
export function useIsDashmoteStaff() {
  const email = useSelector((state) => state.auth.user?.email);
  const accountType = useSelector((state) => state.auth.userData?.account_type);

  // Normalised like `isDashmoteStaffEmail` does, so the fallback cannot be
  // sidestepped by casing alone.
  const looksLikeLegacyDemo = Boolean(
    email?.trim().toLowerCase().includes("demo"),
  );

  return (
    isDashmoteStaffEmail(email) &&
    !isSyntheticAccountType(accountType) &&
    !looksLikeLegacyDemo
  );
}
